import { Table, Input, Select, Button, Typography } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { readLinksOfType, writeLinksOfType, type TopicDetails, type ContentLink } from '@/types/topic';

interface Props {
  details: TopicDetails;
  langs: string[];
  onChange: (d: TopicDetails) => void;
  linkType: string; // 'flyer' | 'attachment' | 'reference'
  label: string;
  urlPlaceholder?: string;
}

/** Edits the topic's links of a single `type`, leaving links of other types untouched. */
export function TypedLinkField({ details, langs, onChange, linkType, label, urlPlaceholder }: Props) {
  const rows = readLinksOfType(details, linkType);
  const commit = (next: ContentLink[]) => onChange(writeLinksOfType(details, linkType, next));
  const update = (i: number, patch: Partial<ContentLink>) =>
    commit(rows.map((l, idx) => (idx === i ? { ...l, ...patch } : l)));
  const add = () => commit([...rows, { url: '', name: '', type: linkType, lang: '' }]);
  const remove = (i: number) => commit(rows.filter((_, idx) => idx !== i));
  const langOptions = [{ value: '', label: 'all' }, ...langs.map((c) => ({ value: c, label: c }))];
  const data = rows.map((l, i) => ({ ...l, __i: i }));

  return (
    <div>
      <Typography.Text strong>{label}</Typography.Text>
      <Table<ContentLink & { __i: number }>
        rowKey="__i"
        size="small"
        pagination={false}
        dataSource={data}
        style={{ marginTop: 8 }}
        locale={{ emptyText: `No ${linkType} links` }}
        columns={[
          { title: 'Name', render: (_, r) => <Input value={r.name} onChange={(e) => update(r.__i, { name: e.target.value })} /> },
          { title: 'URL', render: (_, r) => <Input value={r.url} placeholder={urlPlaceholder} onChange={(e) => update(r.__i, { url: e.target.value })} /> },
          { title: 'Lang', width: 90, render: (_, r) => <Select style={{ width: '100%' }} value={r.lang ?? ''} options={langOptions} onChange={(v) => update(r.__i, { lang: v })} /> },
          { title: '', width: 40, render: (_, r) => <Button size="small" danger icon={<DeleteOutlined />} onClick={() => remove(r.__i)} /> },
        ]}
      />
      <Button size="small" icon={<PlusOutlined />} onClick={add} style={{ marginTop: 8 }}>Add {linkType}</Button>
    </div>
  );
}
